import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import { SystemMutableEntity as SME } from '../../common';
import { UniqueShortId } from '../../common/id';
import { Identifier } from '../../common/identifier';
import { Event } from '../event';
import { IdentityLevel } from '../identityLevel';
import { IdentityNode } from '../identityNode';

import Project = AuthDomain.Object.Project;

import type { SystemCommandContext } from '../common';

const uuidGenerator = UniqueShortId.GetFactory(21);

/**
 * 識別子がどのような経路で取得され、どの程度信頼できるかを表すエンティティ。
 *
 * assuranceLevel は identifierType × eventType の組み合わせから
 * IdentityLevelDefaultConfig を参照して算出される。
 * PJごとのオーバーライド設定がある場合はそちらが優先される（将来実装）。
 */
export type IdentityEvidence = SME & {
  id: IdentityEvidence.Id;
  projectId: Project.Id;
  nodeId: IdentityNode.Id;
  sourceEventId: Event.Id;
  identifierType: Identifier.Type;
  eventType: Event.Type;
  assuranceLevel: IdentityLevel;
  ownerScope: string | undefined;
};

export namespace IdentityEvidence {
  export type Id = Tagged<UniqueShortId, 'identityEvidenceId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export type Props = {
    id: string;
    projectId: string;
    nodeId: string;
    sourceEventId: string;
    identifierType: string;
    eventType: string;
    assuranceLevel: IdentityLevel;
    ownerScope: string | undefined;
  } & SME.Props;

  export const Construct = (props: Props): IdentityEvidence => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      nodeId: IdentityNode.Id.FromString(props.nodeId),
      sourceEventId: Event.Id.FromString(props.sourceEventId),
      identifierType: Identifier.Type.FromString(props.identifierType),
      eventType: Event.Type.FromString(props.eventType),
      assuranceLevel: props.assuranceLevel,
      ownerScope: props.ownerScope,
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<IdentityEvidence.Props, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>;
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }

  export const Create = (params: Create.Params): IdentityEvidence => {
    const { context, props } = params;
    const { projectId, now } = context;
    return Construct({
      id: Id.Generate(),
      projectId,
      ...props,
      ...SME.Create({ date: now }),
    });
  };

  export namespace CreateFromEvent {
    export type Props = {
      event: Event;
      nodeId: IdentityNode.Id;
      identifier: Identifier;
      ownerScope: string | undefined;
    };
    export type Params = {
      context: SystemCommandContext;
      props: CreateFromEvent.Props;
    };
  }

  /**
   * イベントと識別子から IdentityEvidence を作成する。
   * assuranceLevel は identifierType × eventType から自動的に解決される。
   */
  export const CreateFromEvent = (params: CreateFromEvent.Params): IdentityEvidence => {
    const { context, props } = params;

    const { event, nodeId, identifier, ownerScope } = props;
    const assuranceLevel = IdentityLevel.resolveDefault(identifier.type, event.eventType);
    return Create({
      context,
      props: {
        nodeId,
        sourceEventId: event.id,
        identifierType: identifier.type,
        eventType: event.eventType,
        assuranceLevel,
        ownerScope,
      },
    });
  };
}
